import { Checkbox } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import {
  FilterTypes,
  FORM_SCHOOL_RANK_SUB_STATUS,
  FORM_STATUS,
  FormStatusType,
  FormTabsTypes,
  OFFER_STATUS,
  SearchAndFilterTypes,
  WAITLIST_STATUS,
} from "src/constants";
import { useGlossary } from "src/hooks/useGlossary";
import { GlossaryFn } from "src/types/glossary";
import {
  ButtonFilter,
  CommonFilterProps,
  FormFilter,
  FormFilterController,
} from "../CommonFilters";
import { ExpiresAtDateFilter } from "./ExpiresAtDateFilter";
import { StatusDateFilter, SubStatusDateFilter } from "./StatusDateFilter";

export const StatusFilter = ({ filterSettings }: CommonFilterProps) => {
  const { glossary } = useGlossary();
  const options = useMemo(
    () => statusTypesToOptions(FORM_STATUS, glossary),
    [glossary]
  );

  const [hasStatusFilter, setHasStatusFilter] = useState(
    filterSettings.get(SearchAndFilterTypes.Status).length > 0
  );

  return (
    <>
      <FormFilter label="Current status">
        <ButtonFilter
          options={options}
          filterKey={SearchAndFilterTypes.Status}
          filterSettings={filterSettings}
          onChange={(values) => {
            setHasStatusFilter(values.length > 0);
            if (values.length === 0) {
              filterSettings.clear(SearchAndFilterTypes.StatusDaysAgo);
              filterSettings.clear(SearchAndFilterTypes.StatusDateFrom);
              filterSettings.clear(SearchAndFilterTypes.StatusDateTo);
            }
          }}
        />
      </FormFilter>
      {hasStatusFilter ? (
        <FormFilterController filter={FilterTypes.StatusDate}>
          <StatusDateFilter filterSettings={filterSettings} />
        </FormFilterController>
      ) : null}
    </>
  );
};

export const SubStatusFilter = ({ filterSettings }: CommonFilterProps) => {
  const { glossary } = useGlossary();
  const tab = filterSettings.getOne(SearchAndFilterTypes.Tab);
  const options = useMemo(
    () => statusTypesToOptions(getSubStatusOptionsForTab(tab), glossary),
    [tab, glossary]
  );

  const [hasSubStatusFilter, setHasSubStatusFilter] = useState(
    filterSettings.get(SearchAndFilterTypes.SubStatus).length > 0
  );
  const [hasOfferedSubStatus, setHasOfferedSubStatus] = useState(
    filterSettings.get(SearchAndFilterTypes.SubStatus).includes("Offered")
  );
  const [hasExpiresAtFilter, setHasExpiresAtFilter] = useState(
    filterSettings.get(SearchAndFilterTypes.ExpiresAtNextDays).length > 0 ||
      filterSettings.get(SearchAndFilterTypes.ExpiresAtDateFrom).length > 0 ||
      filterSettings.get(SearchAndFilterTypes.ExpiresAtDateTo).length > 0
  );

  return (
    <>
      <FormFilter label="Sub-status">
        <ButtonFilter
          options={options}
          filterKey={SearchAndFilterTypes.SubStatus}
          filterSettings={filterSettings}
          onChange={(values) => {
            setHasSubStatusFilter(values.length > 0);
            setHasOfferedSubStatus(values.includes("Offered"));
            if (values.length === 0) {
              filterSettings.clear(SearchAndFilterTypes.SubStatusDaysAgo);
              filterSettings.clear(SearchAndFilterTypes.SubStatusDateFrom);
              filterSettings.clear(SearchAndFilterTypes.SubStatusDateTo);
            }
            if (!values.includes("Offered")) {
              setHasExpiresAtFilter(false);
              filterSettings.clear(SearchAndFilterTypes.ExpiresAtNextDays);
              filterSettings.clear(SearchAndFilterTypes.ExpiresAtDateFrom);
              filterSettings.clear(SearchAndFilterTypes.ExpiresAtDateTo);
            }
          }}
        />
      </FormFilter>
      {hasSubStatusFilter ? (
        <FormFilterController filter={FilterTypes.SubStatusDate}>
          <SubStatusDateFilter filterSettings={filterSettings} />
        </FormFilterController>
      ) : null}
      {hasSubStatusFilter && hasOfferedSubStatus ? (
        <>
          <FormFilter label="">
            <Checkbox
              isChecked={hasExpiresAtFilter}
              onChange={(e) => {
                setHasExpiresAtFilter(e.target.checked);
                if (!e.target.checked) {
                  filterSettings.clear(SearchAndFilterTypes.ExpiresAtNextDays);
                  filterSettings.clear(SearchAndFilterTypes.ExpiresAtDateFrom);
                  filterSettings.clear(SearchAndFilterTypes.ExpiresAtDateTo);
                }
              }}
            >
              Filter by expiration date
            </Checkbox>
          </FormFilter>
          {hasExpiresAtFilter ? (
            <FormFilterController filter={FilterTypes.ExpiresAtDate}>
              <ExpiresAtDateFilter filterSettings={filterSettings} />
            </FormFilterController>
          ) : null}
        </>
      ) : null}
    </>
  );
};

function statusTypesToOptions(
  statusTypes: Record<string, FormStatusType>,
  glossary: GlossaryFn
) {
  return Object.entries(statusTypes).flatMap(([status, display]) =>
    display.isActive ? { id: status, label: glossary`${display.label}` } : []
  );
}

function getSubStatusOptionsForTab(tab: string | null) {
  switch (tab) {
    case FormTabsTypes.Waitlists:
      return WAITLIST_STATUS;
    case FormTabsTypes.Offers:
      return OFFER_STATUS;
    default:
      return FORM_SCHOOL_RANK_SUB_STATUS;
  }
}
